import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { IClientShippingUI } from 'v2/modules/client/interfaces/client-shipping.interface';
import { CountryConfig } from 'v2/modules/shared/configs/country.config';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { IClientInterface } from 'v2/modules/shared/interfaces/client.interface';
import {
  CheckedItemInterface,
  OptionInterface,
} from 'v2/modules/shared/interfaces/common.interface';
import { ClientShippingApiService } from 'v2/modules/shared/services/api/client/client-shipping-api.service';

@Component({
  selector: 'lum-client-profile-add-shipping-address-form',
  templateUrl: './client-profile-add-shipping-address-form.component.html',
  styleUrls: ['./client-profile-add-shipping-address-form.component.scss'],
})
export class ClientProfileAddShippingAddressFormComponent {
  @Input()
  address :IClientShippingUI;

  @Input()
  client :IClientInterface;

  @Output()
  closeShippingForm = new EventEmitter<boolean>(false);

  form :UntypedFormGroup;
  countryList :OptionInterface[] = [];
  shippingAddresses :IClientShippingUI[] = [];
  countries = CountryConfig.countries;
  loading$ :Observable<boolean>;
  addresses$ :Observable<IClientShippingUI[]>;

  private onDestroy$ = new Subject();

  constructor(
    private snackBar :MatSnackBar,
    private clientShippingApiService :ClientShippingApiService,
    private fb :UntypedFormBuilder,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.setShippingAddressOnEdit();
    this.getDataFromService();
    this.formatCountryList();
    this.getClientShippingFromObservable().subscribe();
  }
  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSameBusinessAddress(option :CheckedItemInterface) :void {
    this.form.patchValue({
      address: option.checked ? this.client?.business_address : '',
      addressLine2: option.checked ? this.client?.business_address_line_2 : '',
      city: option.checked ? this.client?.business_city : '',
      country: option.checked ? this.client?.business_country : '',
      postalCode: option.checked ? this.client?.business_zip : '',
      state: option.checked ? this.client?.business_state : 'US',
      same_with_business: option.checked,
    });
  }
  onSubmit() :void {
    if (this.form.valid && this.client?.business_account_id) {
      if (this.address && this.address.id) {
        this.clientShippingApiService
          .update(this.client.business_account_id, this.address.id, this.form.value)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((response) => {
            const shippingAddresses :IClientShippingUI[] = this.shippingAddresses;

            const index :number = shippingAddresses.findIndex(
              (address :IClientShippingUI) => address.id == this.address.id,
            );
            if (index >= 0) {
              shippingAddresses.splice(index, 1, {
                ...response,
                fullName: this.clientShippingApiService.getFullName(response),
                addressText: this.clientShippingApiService.getAddressText(response),
              });
              this.clientShippingApiService.setShippingAddresses(shippingAddresses);
              this.closeShippingForm.emit(true);
              this.snackBar.open('Successfully updated', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
            }
          });
      } else {
        this.clientShippingApiService
          .create(this.client.business_account_id, this.form.value)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((response) => {
            const shippingAddresses :IClientShippingUI[] = this.shippingAddresses;
            if (shippingAddresses) {
              shippingAddresses.push({
                ...response,
                fullName: this.clientShippingApiService.getFullName(response),
                addressText: this.clientShippingApiService.getAddressText(response),
              });
              this.clientShippingApiService.setShippingAddresses(shippingAddresses);
              this.closeShippingForm.emit(true);
              this.snackBar.open('Successfully added', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
            }
          });
      }
    }
  }

  private initForm() :void {
    this.form = this.fb.group({
      id: [null],
      address: ['', Validators.required],
      addressLine2: [''],
      city: ['', Validators.required],
      companyName: [this.client.business_name, Validators.required],
      country: ['US', Validators.required],
      postalCode: ['', Validators.required],
      state: ['', Validators.required],
      same_with_business: [false],
    });
  }
  private setShippingAddressOnEdit() :void {
    if (this.address) {
      this.form.patchValue(this.address);
    }
  }
  private getDataFromService() :void {
    this.loading$ = this.clientShippingApiService.loading$;
    this.addresses$ = this.clientShippingApiService.addresses$;
  }
  private formatCountryList() :void {
    this.countryList = Object.keys(this.countries).map((index :string) => ({
      value: index,
      label: this.countries[index as keyof typeof this.countries],
    }));
  }
  private getClientShippingFromObservable() :Observable<any> {
    return this.addresses$.pipe(
      tap((addresses :IClientShippingUI[]) => {
        this.shippingAddresses = addresses.map((address) => ({
          ...address,
          fullName: this.clientShippingApiService.getFullName(address),
          addressText: this.clientShippingApiService.getAddressText(address),
        }));
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get country() :OptionInterface | null {
    const code = this.form?.get('country')?.value;
    const index = this.countryList.findIndex((t) => t.value === code);

    return this.countryList[index];
  }
  get hasInvalidInput() :boolean {
    return (
      this.form?.controls['address']?.invalid ||
      this.form?.controls['city']?.invalid ||
      this.form?.controls['companyName']?.invalid ||
      this.form?.controls['country']?.invalid ||
      this.form?.controls['postalCode']?.invalid ||
      this.form?.controls['state']?.invalid
    );
  }
  get isSameWithBusinessAddress() :boolean {
    return this.form?.get('same_with_business')?.value ?? false;
  }
  get addressControl() :UntypedFormControl {
    return this.form.get('address') as UntypedFormControl;
  }
  get cityControl() :UntypedFormControl {
    return this.form.get('city') as UntypedFormControl;
  }
  get companyNameControl() :UntypedFormControl {
    return this.form.get('companyName') as UntypedFormControl;
  }
  get countryControl() :UntypedFormControl {
    return this.form.get('country') as UntypedFormControl;
  }
  get postalCodeControl() :UntypedFormControl {
    return this.form.get('postalCode') as UntypedFormControl;
  }
  get stateControl() :UntypedFormControl {
    return this.form.get('state') as UntypedFormControl;
  }
}
